// src/components/CustomerServiceRequest.tsx

import { Button, Form, Input, Modal, Select, Tooltip } from "antd";
import axios from "axios";
import { HandHelping } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { notify } from "@/components/Notification";
import type { FileData, ProcessingJob } from "@/types";
import { safeStringify } from "@/utils/api";
import { isEligibleForSupport } from "@/utils/documentUtils";

const { Option } = Select;

interface CustomerServiceRequestProps {
  jobId: string;
  job: ProcessingJob;
  processedFiles: FileData[];
  disabled?: boolean;
  size?: "small" | "middle" | "large";
  block?: boolean;
}

const CustomerServiceRequest: React.FC<CustomerServiceRequestProps> = ({
  jobId,
  job,
  processedFiles,
  disabled = false,
  size = "middle",
  block = false,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);
  const eligible = isEligibleForSupport(job, processedFiles);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    try {
      const response = await axios.post("/api/customer-service/request", {
        jobId,
        ...values,
      });

      if (response.status === 201) {
        notify.success({ content: "Your request has been submitted successfully." });
        setIsModalVisible(false);
        form.resetFields();
      } else {
        throw new Error(response.data.message || "Failed to submit request");
      }
    } catch (error) {
      console.error("Error submitting customer service request:", safeStringify(error));
      if (axios.isAxiosError(error) && error.response) {
        notify.error({
          content: error.response.data.message || "Failed to submit request. Please try again.",
        });
      } else {
        notify.error({ content: "An unexpected error occurred. Please try again." });
      }
    }
  };

  return (
    <>
      <Tooltip title="Get Support">
        <Button
          type="text"
          icon={<HandHelping color="#1890ff" size={20} />} // Set color to Ant Design blue and adjust size
          onClick={showModal}
          disabled={disabled || !eligible}
          size={size}
          block={block}
        />
      </Tooltip>
      <Modal
        title="Request Customer Support"
        open={isModalVisible}
        onCancel={handleCancel}
        footer={null}
        width={isMobile ? "100%" : 520}
        style={{ top: isMobile ? 0 : 100 }}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            name="requestReason"
            label="Reason for Request"
            rules={[{ required: true, message: "Please select a reason" }]}
          >
            <Select placeholder="Select a reason">
              <Option value="failed_job">Job failed to complete</Option>
              <Option value="no_output">Job completed but no output generated</Option>
              <Option value="other">Other (please specify in details)</Option>
            </Select>
          </Form.Item>
          <Form.Item name="description" label="Additional Details">
            <Input.TextArea
              rows={isMobile ? 3 : 4}
              placeholder="Provide any additional information that might be helpful"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" block={isMobile}>
              Submit Request
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomerServiceRequest;
